import { useState, useEffect, useRef, useContext } from 'react';
import API from '../utils/API';
import { AuthContext } from '../utils/AuthContext';
import './joblist.css';
import './editJobModal.css';

export default function EditJobModal(props) {
  const { isAdmin } = useContext(AuthContext);

  // Core fields
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);

  // Address
  const [street, setStreet] = useState('');
  const [suite, setSuite] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  // Org typeahead
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState([]);
  const [orgSearching, setOrgSearching] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const orgRef = useRef(null);

  // Capitol / District
  const [officeType, setOfficeType] = useState('');

  // Division
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [divisionSearch, setDivisionSearch] = useState('');
  const [newDivisionName, setNewDivisionName] = useState('');
  const [showDivisionDropdown, setShowDivisionDropdown] = useState(false);
  const divisionRef = useRef(null);

  // Keyboard navigation
  const [highlightedOrgIndex, setHighlightedOrgIndex] = useState(-1);
  const [highlightedDivIndex, setHighlightedDivIndex] = useState(-1);

  // Initialise from props.data whenever a job is opened for editing
  useEffect(() => {
    if (!props.data?.job_id) return;

    setTitle(props.data.title || '');
    setStartDate(props.data.start_date ? props.data.start_date.slice(0, 10) : '');
    setEndDate(props.data.end_date ? props.data.end_date.slice(0, 10) : '');
    setIsCurrent(!!props.data.current_job);
    setStreet(props.data.street_address || props.data.Division?.street_address || props.data.Organization?.street_address || '');
    setSuite(props.data.suite_number || props.data.Division?.suite_number || props.data.Organization?.suite_number || '');
    setCity(props.data.city || props.data.Division?.city || props.data.Organization?.city || '');
    setState(props.data.state || props.data.Division?.state || props.data.Organization?.state || '');
    setZip(String(props.data.zip_code || props.data.Division?.zip_code || props.data.Organization?.zip_code || ''));

    const org = props.data.Organization;
    setOrgSearch(org?.organization_name || '');
    setSelectedOrg(org || null);
    setDivisionId(String(props.data.Division?.division_id || ''));
    setDivisionSearch(props.data.Division?.division_name || '');
    setNewDivisionName('');
    setOfficeType(props.data.office_type || '');

    if (org?.organization_id) {
      API.getDivisionsByOrgId(org.organization_id)
        .then(r => setDivisions(r.data))
        .catch(() => {});
    } else {
      setDivisions([]);
    }
  }, [props.data]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (orgRef.current && !orgRef.current.contains(e.target)) setShowOrgDropdown(false);
      if (divisionRef.current && !divisionRef.current.contains(e.target)) setShowDivisionDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced org search
  useEffect(() => {
    if (orgSearch.length < 2 || selectedOrg) {
      setOrgResults([]);
      setShowOrgDropdown(false);
      return;
    }
    const t = setTimeout(() => {
      setOrgSearching(true);
      setHighlightedOrgIndex(-1);
      API.searchOrganizations(orgSearch)
        .then(r => { setOrgResults(r.data); setShowOrgDropdown(r.data.length > 0); })
        .catch(() => {})
        .finally(() => setOrgSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [orgSearch, selectedOrg]);

  const selectOrg = (org) => {
    setOrgSearch(org.organization_name);
    setShowOrgDropdown(false);
    setOrgResults([]);
    setDivisionId('');
    setDivisionSearch('');
    setNewDivisionName('');
    setShowDivisionDropdown(false);

    Promise.all([
      API.getSingleOrganizations(org.organization_id),
      API.getDivisionsByOrgId(org.organization_id),
    ]).then(([orgRes, divsRes]) => {
      const full = orgRes.data;
      setSelectedOrg(full);
      setDivisions(divsRes.data);
      setStreet(full.street_address || '');
      setSuite(full.suite_number || '');
      setCity(full.city || '');
      setState(full.state || '');
      setZip(full.zip_code || '');
    }).catch(() => {});
  };

  const clearOrg = () => {
    setSelectedOrg(null);
    setOrgSearch('');
    setOrgResults([]);
    setShowOrgDropdown(false);
    setHighlightedOrgIndex(-1);
    setDivisions([]);
    setDivisionId('');
    setDivisionSearch('');
    setNewDivisionName('');
    setShowDivisionDropdown(false);
    setStreet(''); setSuite(''); setCity(''); setState(''); setZip('');
  };

  const filteredDivisions = divisionSearch
    ? divisions.filter(d => d.division_name.toLowerCase().includes(divisionSearch.toLowerCase()))
    : divisions;

  const divShowAddNew = !!(divisionSearch && !filteredDivisions.find(d => d.division_name.toLowerCase() === divisionSearch.toLowerCase()));
  const divItemCount = filteredDivisions.length + (divShowAddNew ? 1 : 0);

  const handleOrgKeyDown = (e) => {
    if (!showOrgDropdown || orgResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedOrgIndex(i => Math.min(i + 1, orgResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedOrgIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedOrgIndex >= 0) {
      e.preventDefault();
      selectOrg(orgResults[highlightedOrgIndex]);
      setHighlightedOrgIndex(-1);
    } else if (e.key === 'Escape') {
      setShowOrgDropdown(false);
      setHighlightedOrgIndex(-1);
    }
  };

  const handleDivKeyDown = (e) => {
    if (!showDivisionDropdown || divItemCount === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedDivIndex(i => Math.min(i + 1, divItemCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedDivIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedDivIndex >= 0) {
      e.preventDefault();
      if (highlightedDivIndex < filteredDivisions.length) {
        selectDivision(filteredDivisions[highlightedDivIndex]);
      } else if (divShowAddNew) {
        confirmNewDivision(divisionSearch);
      }
      setHighlightedDivIndex(-1);
    } else if (e.key === 'Escape') {
      setShowDivisionDropdown(false);
      setHighlightedDivIndex(-1);
    }
  };

  const selectDivision = (d) => {
    handleDivisionChange(String(d.division_id));
    setDivisionSearch(d.division_name);
    setNewDivisionName('');
    setShowDivisionDropdown(false);
  };

  const confirmNewDivision = (name) => {
    setNewDivisionName(name);
    setDivisionId('');
    setShowDivisionDropdown(false);
  };

  const clearDivision = () => {
    setDivisionId('');
    setDivisionSearch('');
    setNewDivisionName('');
    setShowDivisionDropdown(false);
    setStreet(selectedOrg?.street_address || '');
    setSuite(selectedOrg?.suite_number || '');
    setCity(selectedOrg?.city || '');
    setState(selectedOrg?.state || '');
    setZip(selectedOrg?.zip_code || '');
  };

  const handleDivisionChange = (divId) => {
    setDivisionId(divId);
    if (!divId) {
      setStreet(selectedOrg?.street_address || '');
      setSuite(selectedOrg?.suite_number || '');
      setCity(selectedOrg?.city || '');
      setState(selectedOrg?.state || '');
      setZip(selectedOrg?.zip_code || '');
      return;
    }
    const div = divisions.find(d => String(d.division_id) === String(divId));
    if (div) {
      setStreet(div.street_address || selectedOrg?.street_address || '');
      setSuite(div.suite_number || selectedOrg?.suite_number || '');
      setCity(div.city || selectedOrg?.city || '');
      setState(div.state || selectedOrg?.state || '');
      setZip(div.zip_code || selectedOrg?.zip_code || '');
    }
  };

  const isValid = !!(title && selectedOrg && startDate && (isCurrent || endDate) && street && city && state && zip);

  const handleUpdate = async () => {
    try {
      const data = {
        job_id: props.data.job_id,
        title,
        organization_id: selectedOrg.organization_id,
        street_address: street,
        suite_number: suite,
        city,
        state,
        zip_code: zip,
        start_date: startDate,
        current_job: isCurrent,
      };
      if (endDate) data.end_date = endDate;

      data.office_type = officeType || null;

      if (divisionId) {
        data.division_id = divisionId;
      } else if (newDivisionName && data.organization_id) {
        await API.addDivision({
          division_name: newDivisionName,
          street_address: street,
          city,
          state,
          zip_code: zip,
          organization_id: data.organization_id,
        });
        const divs = await API.getDivisionsByOrgId(data.organization_id);
        const match = divs.data.find(d => d.division_name === newDivisionName);
        if (match) data.division_id = match.division_id;
      }

      await API.updateJobExperience(data);
      props.reRenderProfile();
    } catch (err) {
      console.error('Update job failed:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await API.deleteJobExperience({ job_id: props.data.job_id });
      props.reRenderProfile();
    } catch (err) {
      console.error('Delete job failed:', err);
    }
  };

  const toProper = (s) => s.replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="modal fade" id="editJobModal" data-bs-backdrop="static" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-scrollable">
        <div className="modal-content">

          <div className="jm-modal-header">
            <h5 className="jm-modal-title"><i className="bi bi-pencil-square me-2" />Edit Job Experience</h5>
          </div>

          <div className="modal-body jm-body">

            {/* Title */}
            <div className="jm-field">
              <label className="jm-label">Title <span className="jm-req">*</span></label>
              <input className="jm-input" value={title} onChange={e => setTitle(e.target.value)} />
            </div>

            {/* Org + Division */}
            <div className="jm-grid-2">
              <div className="jm-field">
                <label className="jm-label">Organization <span className="jm-req">*</span></label>
                <div className="jm-typeahead" ref={orgRef}>
                  <div className="jm-typeahead-wrap">
                    <input
                      className="jm-input"
                      placeholder="Search organizations…"
                      value={orgSearch}
                      onChange={e => { setOrgSearch(e.target.value); if (selectedOrg) clearOrg(); }}
                      onFocus={() => orgResults.length > 0 && setShowOrgDropdown(true)}
                      onKeyDown={handleOrgKeyDown}
                      autoComplete="off"
                    />
                    {orgSearching && <span className="jm-spinner" />}
                    {selectedOrg && (
                      <button type="button" className="jm-typeahead-clear" onClick={clearOrg} title="Clear">
                        <i className="bi bi-x-lg" />
                      </button>
                    )}
                  </div>
                  {showOrgDropdown && orgResults.length > 0 && (
                    <div className="jm-typeahead-dropdown">
                      {orgResults.map((org, idx) => (
                        <button
                          type="button"
                          key={org.organization_id}
                          className={`jm-typeahead-item${idx === highlightedOrgIndex ? ' jm-typeahead-item--highlighted' : ''}`}
                          onMouseDown={() => selectOrg(org)}
                          onMouseEnter={() => setHighlightedOrgIndex(idx)}
                        >
                          <span className="jm-typeahead-name">{org.organization_name}</span>
                          {org.group_type && <span className="jm-typeahead-meta">{org.group_type}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="jm-field">
                <label className="jm-label">Division <span className="jm-opt">optional</span></label>
                <div className="jm-typeahead" ref={divisionRef}>
                  <div className="jm-typeahead-wrap">
                    <input
                      className="jm-input"
                      placeholder={!selectedOrg ? 'Select org first' : divisions.length ? 'Search divisions…' : 'Type to add a division'}
                      value={divisionSearch}
                      disabled={!selectedOrg}
                      onChange={e => {
                        const val = e.target.value;
                        setDivisionSearch(val);
                        setDivisionId('');
                        setNewDivisionName('');
                        setShowDivisionDropdown(true);
                        setHighlightedDivIndex(-1);
                      }}
                      onKeyDown={handleDivKeyDown}
                      onFocus={() => setShowDivisionDropdown(true)}
                      autoComplete="off"
                    />
                    {(divisionId || newDivisionName || divisionSearch) && (
                      <button type="button" className="jm-typeahead-clear" onClick={clearDivision} title="Clear">
                        <i className="bi bi-x-lg" />
                      </button>
                    )}
                  </div>
                  {newDivisionName && !divisionId && (
                    <div className="jm-division-new-tag">
                      <i className="bi bi-plus-circle me-1" />New division: {newDivisionName}
                    </div>
                  )}
                  {showDivisionDropdown && selectedOrg && (
                    <div className="jm-typeahead-dropdown">
                      {filteredDivisions.map((d, idx) => (
                        <button
                          type="button"
                          key={d.division_id}
                          className={`jm-typeahead-item${idx === highlightedDivIndex ? ' jm-typeahead-item--highlighted' : ''}`}
                          onMouseDown={() => selectDivision(d)}
                          onMouseEnter={() => setHighlightedDivIndex(idx)}
                        >
                          <span className="jm-typeahead-name">{d.division_name}</span>
                        </button>
                      ))}
                      {divShowAddNew && (
                        <button
                          type="button"
                          className={`jm-typeahead-item jm-typeahead-item--add${highlightedDivIndex === filteredDivisions.length ? ' jm-typeahead-item--highlighted' : ''}`}
                          onMouseDown={() => confirmNewDivision(divisionSearch)}
                          onMouseEnter={() => setHighlightedDivIndex(filteredDivisions.length)}
                        >
                          <i className="bi bi-plus me-1" />Add "{divisionSearch}" as new division
                        </button>
                      )}
                      {!divisionSearch && filteredDivisions.length === 0 && (
                        <div className="jm-typeahead-empty">No divisions — type to add one</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="jm-address-block">
              <p className="jm-address-note">
                <i className="bi bi-info-circle me-1" />
                Address auto-filled from the selected organization or division — edit as needed.
              </p>
              <div className="jm-grid-2">
                <div className="jm-field">
                  <label className="jm-label">Street Address</label>
                  <input className="jm-input" value={street} onChange={e => setStreet(toProper(e.target.value))} />
                </div>
                <div className="jm-field">
                  <label className="jm-label">Suite / Unit <span className="jm-opt">optional</span></label>
                  <input className="jm-input" value={suite} onChange={e => setSuite(e.target.value)} />
                </div>
              </div>
              <div className="jm-grid-3">
                <div className="jm-field">
                  <label className="jm-label">City</label>
                  <input className="jm-input" value={city} onChange={e => setCity(toProper(e.target.value))} />
                </div>
                <div className="jm-field">
                  <label className="jm-label">State</label>
                  <input className="jm-input" value={state} maxLength={2} onChange={e => setState(e.target.value.toUpperCase())} />
                </div>
                <div className="jm-field">
                  <label className="jm-label">ZIP</label>
                  <input className="jm-input" value={zip} onChange={e => setZip(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="jm-grid-2">
              <div className="jm-field">
                <label className="jm-label">Start Date <span className="jm-req">*</span></label>
                <input className="jm-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="jm-field">
                <label className="jm-label">End Date <span className="jm-opt">or current</span></label>
                <input className="jm-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={isCurrent} />
              </div>
            </div>
            <label className="jm-current-pos">
              <input
                type="checkbox"
                checked={isCurrent}
                onChange={e => { setIsCurrent(e.target.checked); if (e.target.checked) setEndDate(''); }}
              />
              Current Position
            </label>

            {(selectedOrg?.organization_id === 64 || selectedOrg?.organization_id === 65) && (
              <div className="jm-field mt-3">
                <label className="jm-label">Office Location <span className="jm-opt">Capitol or District</span></label>
                <select
                  className="jm-select"
                  value={officeType}
                  onChange={e => setOfficeType(e.target.value)}
                >
                  <option value="">— Not set —</option>
                  {isAdmin && <option value="Elected">Elected</option>}
                  <option value="Capitol">Capitol</option>
                  <option value="District">District</option>
                </select>
              </div>
            )}

          </div>

          <div className="jm-modal-footer">
            {!isCurrent && props.expLength > 1 && (
              <button
                type="button"
                className="job-delete"
                data-bs-dismiss="modal"
                onClick={handleDelete}
                title="Delete this job"
              >
                <i className="bi bi-trash3" />
              </button>
            )}
            <button type="button" className="jm-btn-ghost" data-bs-dismiss="modal">Close</button>
            <button
              type="button"
              className="jm-btn-primary"
              data-bs-dismiss="modal"
              onClick={handleUpdate}
              disabled={!isValid}
            >
              Update
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
